import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { X, Calendar, Clock, User, FileText } from 'lucide-react'
import { useCreateEvent } from '@/hooks/useCalendarEvents'
import type { CreateEventPayload, CalendarEventType } from '@/types/calendar'
import type { Employee } from '@/types/workforce'
import { addMinutes, format } from 'date-fns'
import RecurrenceConfig from './RecurrenceConfig'

// Form validation schema
const createEventSchema = z.object({
  type: z.enum(['1-1', 'pdi'] as const),
  employeeId: z.string().min(1, 'Selecione um funcionário'),
  title: z.string().optional(),
  startDate: z.string().min(1, 'Data e hora são obrigatórias'),
  duration: z.number().min(15).max(480),
  description: z.string().max(500).optional()
})

type CreateEventFormData = z.infer<typeof createEventSchema>

interface EventCreationModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: (event: any) => void
  defaultValues?: Partial<CreateEventPayload>
  subordinates: Employee[]
}

export default function EventCreationModal({
  open,
  onClose,
  onSuccess,
  defaultValues,
  subordinates
}: EventCreationModalProps) {
  const { mutate: createEvent, isPending, error: mutationError } = useCreateEvent()
  const [apiError, setApiError] = useState<string | null>(null)
  const [rrule, setRrule] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
    reset
  } = useForm<CreateEventFormData>({
    resolver: zodResolver(createEventSchema),
    defaultValues: {
      type: '1-1',
      duration: 60,
      startDate: getDefaultStartDate(),
      title: '',
      description: ''
    }
  })

  // Watch form values for dynamic behavior
  const selectedType = watch('type')
  const selectedEmployeeId = watch('employeeId')

  // Helper to get default start date (next hour)
  function getDefaultStartDate(): string {
    const now = new Date()
    const nextHour = new Date(now)
    nextHour.setHours(now.getHours() + 1, 0, 0, 0)
    return format(nextHour, "yyyy-MM-dd'T'HH:mm")
  }

  // Auto-generate title when employee or type changes
  useEffect(() => {
    if (selectedEmployeeId && !watch('title')) {
      const employee = subordinates.find(e => e.employeeId === selectedEmployeeId)
      if (employee) {
        const titlePrefix = selectedType === '1-1' ? '1:1 com' : 'PDI -'
        setValue('title', `${titlePrefix} ${employee.personalInfo.name}`)
      }
    }
  }, [selectedEmployeeId, selectedType, subordinates, setValue, watch])

  // Reset form and errors when modal opens/closes
  useEffect(() => {
    if (!open) {
      reset()
      setApiError(null)
      setRrule(null)
    }
  }, [open, reset])

  const onSubmit = (data: CreateEventFormData) => {
    setApiError(null)

    const employee = subordinates.find(e => e.employeeId === data.employeeId)
    if (!employee) {
      setApiError('Funcionário não encontrado')
      return
    }

    // Calculate end date based on duration
    const startDate = new Date(data.startDate)
    const endDate = addMinutes(startDate, data.duration)

    // Generate title if empty
    const title = data.title || `${data.type === '1-1' ? '1:1' : 'PDI'} com ${employee.personalInfo.name}`

    const payload: CreateEventPayload = {
      type: data.type as CalendarEventType,
      employeeId: data.employeeId,
      title,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      allDay: false,
      description: data.description,
      rrule: rrule || undefined
    }

    createEvent(payload, {
      onSuccess: (event) => {
        onSuccess?.(event)
        onClose()
        reset()
      },
      onError: (error: Error) => {
        // Map API errors to user-friendly messages
        if (error.message.includes('403') || error.message.includes('Forbidden')) {
          setApiError('Você não tem permissão para criar eventos para este funcionário.')
        } else if (error.message.includes('400') || error.message.includes('Bad Request')) {
          setApiError('Dados inválidos. Verifique os campos.')
        } else if (error.message.includes('500') || error.message.includes('Server Error')) {
          setApiError('Erro no servidor. Tente novamente em alguns instantes.')
        } else {
          setApiError('Erro de conexão. Verifique sua internet.')
        }
      }
    })
  }

  const handleClose = () => {
    if (isDirty && !window.confirm('Você tem alterações não salvas. Deseja sair mesmo assim?')) {
      return
    }
    onClose()
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose()
      }}
    >
      <div className="bg-brand-dark-light rounded-monumenta max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-brand-gray/20 shadow-monumenta-xl animate-scale-in">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-monumenta bg-brand-primary/20">
                <Calendar size={24} className="text-brand-primary" />
              </div>
              <div>
                <h2 className="font-display text-xl font-bold text-white">
                  Criar Novo Evento
                </h2>
                <p className="text-brand-gray text-sm">
                  Agende uma reunião 1:1 ou sessão de PDI
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-monumenta bg-brand-dark hover:bg-brand-dark-lighter text-brand-gray hover:text-white transition-all duration-200"
            >
              <X size={20} />
            </button>
          </div>

          {/* Error Alert */}
          {apiError && (
            <div className="mb-6 bg-red-50/10 border border-red-500/20 rounded-monumenta p-4">
              <p className="text-red-400 text-sm">{apiError}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Event Type */}
            <div>
              <label className="block font-display font-medium text-white mb-2">
                Tipo de Evento *
              </label>
              <div className="flex gap-3">
                <label className="flex-1">
                  <input
                    type="radio"
                    value="1-1"
                    {...register('type')}
                    className="sr-only peer"
                  />
                  <div className="p-4 rounded-monumenta border-2 border-brand-gray/20 bg-brand-dark-lighter cursor-pointer transition-all peer-checked:border-brand-primary peer-checked:bg-brand-primary/10 hover:border-brand-gray/40">
                    <div className="flex items-center gap-2">
                      <User size={16} className="text-brand-primary" />
                      <span className="font-medium text-white">1:1 Meeting</span>
                    </div>
                  </div>
                </label>
                <label className="flex-1">
                  <input
                    type="radio"
                    value="pdi"
                    {...register('type')}
                    className="sr-only peer"
                  />
                  <div className="p-4 rounded-monumenta border-2 border-brand-gray/20 bg-brand-dark-lighter cursor-pointer transition-all peer-checked:border-brand-primary peer-checked:bg-brand-primary/10 hover:border-brand-gray/40">
                    <div className="flex items-center gap-2">
                      <FileText size={16} className="text-brand-primary" />
                      <span className="font-medium text-white">PDI Session</span>
                    </div>
                  </div>
                </label>
              </div>
              {errors.type && (
                <p className="mt-1 text-sm text-red-400">{errors.type.message}</p>
              )}
            </div>

            {/* Employee Selection */}
            <div>
              <label className="block font-display font-medium text-white mb-2">
                Colaborador *
              </label>
              <select
                {...register('employeeId')}
                className="w-full px-4 py-3 rounded-monumenta bg-brand-dark-lighter border border-brand-gray/20 text-white focus:border-brand-primary focus:outline-none transition-all"
              >
                <option value="">Selecione um colaborador</option>
                {subordinates.map((employee) => (
                  <option key={employee.employeeId} value={employee.employeeId}>
                    {employee.personalInfo.name} - {employee.currentEngagement?.departmentName || 'N/A'}
                  </option>
                ))}
              </select>
              {errors.employeeId && (
                <p className="mt-1 text-sm text-red-400">{errors.employeeId.message}</p>
              )}
            </div>

            {/* Title (Optional) */}
            <div>
              <label className="block font-display font-medium text-white mb-2">
                Título (opcional)
              </label>
              <input
                type="text"
                {...register('title')}
                placeholder={`${selectedType === '1-1' ? '1:1' : 'PDI'} com ${subordinates.find(e => e.employeeId === selectedEmployeeId)?.personalInfo.name || 'colaborador'}`}
                maxLength={100}
                className="w-full px-4 py-3 rounded-monumenta bg-brand-dark-lighter border border-brand-gray/20 text-white placeholder-brand-gray/50 focus:border-brand-primary focus:outline-none transition-all"
              />
              <p className="mt-1 text-xs text-brand-gray">
                Deixe em branco para gerar automaticamente
              </p>
            </div>

            {/* Date and Time */}
            <div>
              <label className="block font-display font-medium text-white mb-2">
                Data e Hora *
              </label>
              <input
                type="datetime-local"
                {...register('startDate')}
                className="w-full px-4 py-3 rounded-monumenta bg-brand-dark-lighter border border-brand-gray/20 text-white focus:border-brand-primary focus:outline-none transition-all"
              />
              {errors.startDate && (
                <p className="mt-1 text-sm text-red-400">{errors.startDate.message}</p>
              )}
            </div>

            {/* Duration */}
            <div>
              <label className="block font-display font-medium text-white mb-2">
                Duração *
              </label>
              <select
                {...register('duration', { valueAsNumber: true })}
                className="w-full px-4 py-3 rounded-monumenta bg-brand-dark-lighter border border-brand-gray/20 text-white focus:border-brand-primary focus:outline-none transition-all"
              >
                <option value={15}>15 minutos</option>
                <option value={30}>30 minutos</option>
                <option value={45}>45 minutos</option>
                <option value={60}>1 hora</option>
                <option value={90}>1 hora e 30 minutos</option>
                <option value={120}>2 horas</option>
              </select>
            </div>

            {/* Description */}
            <div>
              <label className="block font-display font-medium text-white mb-2">
                Descrição (opcional)
              </label>
              <textarea
                {...register('description')}
                placeholder="Agenda, tópicos a discutir..."
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 rounded-monumenta bg-brand-dark-lighter border border-brand-gray/20 text-white placeholder-brand-gray/50 focus:border-brand-primary focus:outline-none transition-all resize-none"
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-400">{errors.description.message}</p>
              )}
            </div>

            {/* Recurrence Configuration */}
            <div className="pt-4 border-t border-brand-gray/20">
              <RecurrenceConfig
                value={rrule}
                onChange={setRrule}
                startDate={watch('startDate') ? new Date(watch('startDate')) : new Date()}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-brand-gray/20">
              <button
                type="button"
                onClick={handleClose}
                disabled={isPending}
                className="flex-1 btn-secondary"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isPending}
                className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Criando...
                  </span>
                ) : (
                  'Criar Evento'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
